import { useEffect, useRef, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  ListGroup,
  Spinner,
  Table,
  Button,
} from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./SalarySlipPdf.css";
import { fetchStationsByResourceId } from "../Api/ResourceMaster";
import { fetchCompanyByCompanyCode } from "../Api/ResourceMaster";

import { useAuth } from "../Context/AuthContext";
import { getResourcesByOrgId } from "../Api/HrOrgDocumentMasterApi";
import { getSalarySlip, getAvailableSalaryPeriods } from "../Api/ViewSalary";
import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";
import { fetchResourceById } from "../Api/ResourceMaster";
import { getDepartmentListByCode } from "../Api/DepartmentList";

const SalarySlipHr = () => {
  const { user } = useAuth();
  const slipRef = useRef(null);

  const [resources, setResources] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null);
  const [companyAddress, setCompanyAddress] = useState(null);
  const [departmentDetails, setDepartmentDetails] = useState(null);

  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stationDetails, setStationDetails] = useState(null);

  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);

  const [resourceDetails, setResourceDetails] = useState(null);

  // =========================
  // Load Employees
  // =========================
  // const loadResources = async () => {
  //   try {
  //     const data = await getResourcesByOrgId(user?.organisationId);
  //     setResources(
  //       (data || []).map((r) => ({
  //         label: `${r.name} (${r.resourceId})`,
  //         value: r.resourceId,
  //         resourceId: r.resourceId,
  //       })),
  //     );
  //   } catch {
  //     toast.error("Failed to load employees");
  //   }
  // };

  const loadResources = async () => {
    try {
      const data = await getResourcesByOrgId(user?.organisationId);

      const mappedResources = (data || []).map((r) => ({
        label: `${r.name} (${r.resourceId})`,
        value: r.resourceId,
        resourceId: r.resourceId,
        mavUserId: r.mavUserId, // 👈 important
      }));

      setResources(mappedResources);

      // ✅ Auto-select logged-in user
      const loggedInResource = mappedResources.find(
        (r) => r.mavUserId === user.userId,
      );

      if (loggedInResource) {
        setSelectedResource(loggedInResource);
        loadAvailablePeriods(loggedInResource.resourceId);
      }
    } catch {
      toast.error("Failed to load employees");
    }
  };

  const loadAvailablePeriods = async (resourceId) => {
    try {
      const data = await getAvailableSalaryPeriods({
        orgId: user.organisationId,
        resourceId,
      });

      console.log("Available Periods:", data); // Debug

      setAvailablePeriods(data);

      const uniqueYears = [...new Set(data.map((x) => x.year))];

      setAvailableYears(
        uniqueYears.map((y) => ({
          value: y,
          label: `Year ${y}`,
        })),
      );
    } catch {
      toast.error("No salary data available");
    }
  };

  // useEffect(() => {
  //   const loadResourceDetails = async () => {
  //     if (!selectedResource?.resourceId) return;

  //     try {
  //       const data = await fetchResourceById(selectedResource.resourceId);
  //       console.log("ResourceMaster API Data:", data);
  //       setResourceDetails(data); // ✅ ADD THIS
  //     } catch (err) {
  //       console.error("Error fetching resource by ID:", err);
  //     }
  //   };

  //   loadResourceDetails();
  // }, [selectedResource]);

  useEffect(() => {
    const loadResourceAndCompany = async () => {
      if (!selectedResource?.resourceId) return;

      try {
        // 1️⃣ Resource details
        const resource = await fetchResourceById(selectedResource.resourceId);
        setResourceDetails(resource);

        // 2️⃣ Company + Address using companyCode
        if (resource?.companyCode) {
          const res = await fetchCompanyByCompanyCode(resource.companyCode);

          if (res?.success) {
            setCompany(res.data.company);
            setCompanyAddress(res.data.address);
          }
        }
      } catch (err) {
        console.error("Error loading resource/company:", err);
        setCompany(null);
        setCompanyAddress(null);
      }
    };

    loadResourceAndCompany();
  }, [selectedResource]);

  useEffect(() => {
    const loadStationDetails = async () => {
      if (!selectedResource?.resourceId) return;

      try {
        const data = await fetchStationsByResourceId(
          selectedResource.resourceId,
        );

        console.log("Station API Data:", data);

        // API returns array → take first station
        setStationDetails(data?.[0] || null);
      } catch (err) {
        console.error("Error fetching station details:", err);
        setStationDetails(null);
      }
    };

    loadStationDetails();
  }, [selectedResource]);

  useEffect(() => {
    const loadDepartmentDetails = async () => {
      const deptCode = salaryData?.employee?.department;
      if (!deptCode) return;

      try {
        const data = await getDepartmentListByCode(deptCode);
        console.log("Department API Data:", data);
        setDepartmentDetails(data);
      } catch (err) {
        console.error("Error fetching department details:", err);
        setDepartmentDetails(null);
      }
    };

    loadDepartmentDetails();
  }, [salaryData]);

  // =========================
  // Load Companies
  // =========================
  const loadCompanies = async () => {
    const data = await getAllCompanyCodes(user.organisationId);
    console.log("Loaded Companies:", data); // Debug
    setCompanies(data || []);
  };

  useEffect(() => {
    if (user?.organisationId) {
      loadResources();
      loadCompanies();
    }
  }, [user?.organisationId]);

  // =========================
  // Helpers
  // =========================
  const yearOptions = Array.from({ length: 5 }).map((_, i) => {
    const y = moment().year() - i;
    return { value: y, label: `Year ${y}` };
  });

  const months = selectedYear
    ? Array.from({ length: 12 }).map((_, i) => {
        const m = 11 - i;
        return {
          month: String(m + 1).padStart(2, "0"),
          label: moment().year(selectedYear.value).month(m).format("MMMM YYYY"),
        };
      })
    : [];

  const extractCompanyCode = (workingDaysId) => workingDaysId.substring(4, 8);

  // =========================
  // Load Salary Slip
  // =========================
  const loadSalarySlip = async (month) => {
    try {
      setLoading(true);
      setSelectedMonth(month);

      const data = await getSalarySlip({
        orgId: user.organisationId,
        resourceId: selectedResource.resourceId,
        year: selectedYear.value,
        month: month.month,
      });

      console.log("Salary Data:", data); // Debug

      setSalaryData(data);

      const companyCode = extractCompanyCode(data.payroll.workingDaysId);
      const matchedCompany = companies.find(
        (c) => c.companyCode === companyCode,
      );
      setCompany(matchedCompany || null);
    } catch (err) {
      toast.error(err);
      setSalaryData(null);
      setCompany(null);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // PDF DOWNLOAD
  // =========================
  const downloadPdf = async () => {
    if (!slipRef.current) return;

    const canvas = await html2canvas(slipRef.current, {
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

    const fileName = `SalarySlip_${salaryData.employee.resourceId}_${selectedMonth.label}.pdf`;
    pdf.save(fileName);
  };

  // =========================
  // Number to Words (Indian)
  // =========================
  const numberToWords = (num) => {
    if (num === 0) return "Zero";

    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const convert = (n) => {
      if (n < 20) return a[n];
      if (n < 100)
        return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
      if (n < 1000)
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " and " + convert(n % 100) : "")
        );
      return "";
    };

    let words = "";

    const crore = Math.floor(num / 10000000);
    num %= 10000000;

    const lakh = Math.floor(num / 100000);
    num %= 100000;

    const thousand = Math.floor(num / 1000);
    num %= 1000;

    if (crore) words += convert(crore) + " Crore ";
    if (lakh) words += convert(lakh) + " Lakh ";
    if (thousand) words += convert(thousand) + " Thousand ";
    if (num) words += convert(num);

    return words.trim();
  };

  return (
    <div className="main-content expand">
      <Container fluid>
        <h1 className="header-list-title">Salary Slip</h1>

        {/* EMPLOYEE SELECT */}
        <Card className="p-3 mt-3">
          <Select
            options={resources}
            value={selectedResource}
            onChange={(v) => {
              setSelectedResource(v);
              setSelectedYear(null);
              setSelectedMonth(null);
              setSalaryData(null);
              setCompany(null);
              setAvailableMonths([]);
              setAvailableYears([]);
              setAvailablePeriods([]);

              if (v?.resourceId) {
                loadAvailablePeriods(v.resourceId); // 🔥 THIS WAS MISSING
              }
            }}
            placeholder="Select Employee"
            isClearable
          />
        </Card>

        {selectedResource && (
          <Row className="mt-3">
            {/* LEFT */}
            <Col md={4}>
              <Card className="p-3">
                <Select
                  options={availableYears}
                  value={selectedYear}
                  onChange={(v) => {
                    setSelectedYear(v);
                    setSelectedMonth(null);
                    setSalaryData(null);

                    const monthsForYear = availablePeriods
                      .filter((p) => p.year === v.value)
                      .map((p) => ({
                        month: p.month,
                        label: moment()
                          .month(parseInt(p.month, 10) - 1)
                          .year(v.value)
                          .format("MMMM YYYY"),
                      }));

                    setAvailableMonths(monthsForYear);
                  }}
                  placeholder="Select Year"
                />

                {selectedYear && (
                  <ListGroup className="mt-3">
                    {availableMonths.map((m) => (
                      <ListGroup.Item
                        key={m.label}
                        action
                        active={selectedMonth?.label === m.label}
                        onClick={() => loadSalarySlip(m)}
                      >
                        {m.label}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card>
            </Col>

            {/* RIGHT */}
            <Col md={8}>
              <Card className="p-4">
                {loading ? (
                  <Spinner />
                ) : salaryData ? (
                  <>
                    {/* DOWNLOAD BUTTON */}
                    <div className="d-flex justify-content-end mb-3">
                      <Button onClick={downloadPdf}>Download PDF</Button>
                    </div>

                    {/* SLIP CONTENT */}
                    <div ref={slipRef} className="payslip-wrapper">
                      {/* HEADER */}
                      <div className="payslip-header">
                        {company?.logoUrl && (
                          <img
                            src={company.logoUrl}
                            alt="logo"
                            className="company-logo"
                          />
                        )}

                        <h4 className="company-name">{company?.name}</h4>

                        {companyAddress && (
                          <p className="company-address">
                            {[
                              companyAddress.address1,
                              companyAddress.address2,
                              companyAddress.address3,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </p>
                        )}

                        <p className="company-address">
                          Payslip for the month of {selectedMonth.label}
                        </p>
                      </div>

                      {/* EMPLOYEE INFO */}
                      <table className="info-table">
                        <tbody>
                          <tr>
                            <td>
                              <strong>Name</strong>
                            </td>
                            <td>{salaryData.employee.name}</td>
                            <td>
                              <strong>Employee No</strong>
                            </td>
                            <td>{salaryData.employee.resourceId}</td>
                          </tr>

                          <tr>
                            <td>
                              <strong>Designation</strong>
                            </td>
                            <td>{resourceDetails?.role || "-"}</td>
                            <td>
                              <strong>Employee Type</strong>
                            </td>
                            <td>{resourceDetails?.employeeType || "-"}</td>
                          </tr>

                          <tr>
                            <td>
                              <strong>Department</strong>
                            </td>
                            <td>
                              {departmentDetails?.deptDescription ||
                                salaryData.employee.department}
                            </td>
                            <td>
                              <strong>Office</strong>
                            </td>
                            <td>{resourceDetails?.officeName || "-"}</td>
                          </tr>

                          <tr>
                            <td>
                              <strong>Station</strong>
                            </td>
                            <td>{stationDetails?.stationName || "-"}</td>
                            {/* <td>
                              <strong>Organisation ID</strong>
                            </td>
                            <td>{stationDetails?.organistionId || "-"}</td> */}
                          </tr>

                          <tr>
                            <td>
                              <strong>Email</strong>
                            </td>
                            <td>{salaryData.employee.emailId}</td>
                            <td>
                              <strong>Mobile</strong>
                            </td>
                            <td>{resourceDetails?.phoneNumber || "-"}</td>
                          </tr>

                          <tr>
                            <td>
                              <strong>Bank Name</strong>
                            </td>
                            <td>{resourceDetails?.bankName || "-"}</td>
                            <td>
                              <strong>Account No</strong>
                            </td>
                            <td>{resourceDetails?.accountNo || "-"}</td>
                          </tr>

                          <tr>
                            <td>
                              <strong>IFSC Code</strong>
                            </td>
                            <td>{resourceDetails?.ifscCode || "-"}</td>
                            <td>
                              <strong>PAN No</strong>
                            </td>
                            <td>{resourceDetails?.panNumber || "-"}</td>
                          </tr>

                          <tr>
                            <td>
                              <strong>UAN No</strong>
                            </td>
                            <td>{resourceDetails?.uanNumber || "-"}</td>
                            <td>
                              <strong>Date of Joining</strong>
                            </td>
                            <td>
                              {resourceDetails?.doj
                                ? moment(resourceDetails.doj).format(
                                    "DD MMM YYYY",
                                  )
                                : "-"}
                            </td>
                          </tr>

                          <tr>
                            <td>
                              <strong>Salary Days</strong>
                            </td>
                            <td>{salaryData.payroll.salaryDays}</td>
                            <td>
                              <strong>Working Days</strong>
                            </td>
                            <td>{salaryData.payroll.workingDays}</td>
                          </tr>
                        </tbody>
                      </table>

                      {/* EARNINGS & DEDUCTIONS */}
                      <div className="earnings-deductions">
                        <table>
                          <thead>
                            <tr>
                              <th colSpan="2">Earnings</th>
                              <th colSpan="2">Deductions</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Basic + DA</td>
                              <td>₹{salaryData.payroll.basicPlusDa}</td>
                              <td>PF</td>
                              <td>₹{salaryData.payroll.pf}</td>
                            </tr>
                            <tr>
                              <td>HRA</td>
                              <td>₹{salaryData.payroll.hra}</td>
                              <td>ESIC</td>
                              <td>₹{salaryData.payroll.esic}</td>
                            </tr>
                            <tr>
                              <td>Other Allowance</td>
                              <td>₹{salaryData.payroll.otherAllowance}</td>
                              <td>TDS</td>
                              <td>₹{salaryData.payroll.tdsdeductionAmount}</td>
                            </tr>
                            <tr className="total-row">
                              <td>
                                <strong>Total Earnings</strong>
                              </td>
                              <td>
                                <strong>₹{salaryData.payroll.gross}</strong>
                              </td>
                              <td>
                                <strong>Total Deductions</strong>
                              </td>
                              <td>
                                <strong>
                                  ₹{salaryData.payroll.totalDeductions}
                                </strong>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* NET PAY */}
                      <div className="netpay">
                        <strong>
                          Net Pay for the month: ₹
                          {salaryData.payroll.netPayable}
                        </strong>

                        <div className="netpay-words">
                          <em>
                            (Rupees{" "}
                            {numberToWords(
                              Math.floor(salaryData.payroll.netPayable),
                            )}{" "}
                            Only)
                          </em>
                        </div>
                      </div>

                      {/* FOOTER */}
                      <div className="payslip-footer">
                        This is a computer generated payslip and does not
                        require a signature
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-muted text-center">
                    Select a month to view salary slip
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default SalarySlipHr;

// import { useEffect, useState } from "react";
// import {
//   Container,
//   Card,
//   Row,
//   Col,
//   ListGroup,
//   Spinner,
//   Table,
// } from "react-bootstrap";
// import Select from "react-select";
// import { toast } from "react-toastify";
// import moment from "moment";

// import { useAuth } from "../Context/AuthContext";
// import { getResourcesByOrgId } from "../Api/HrOrgDocumentMasterApi";
// import { getSalarySlip } from "../Api/ViewSalary";
// import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";

// const SalarySlipHr = () => {
//   const { user } = useAuth();

//   const [resources, setResources] = useState([]);
//   const [companies, setCompanies] = useState([]);
//   const [company, setCompany] = useState(null);

//   const [selectedResource, setSelectedResource] = useState(null);
//   const [selectedYear, setSelectedYear] = useState(null);
//   const [selectedMonth, setSelectedMonth] = useState(null);

//   const [salaryData, setSalaryData] = useState(null);
//   const [loading, setLoading] = useState(false);

//   // =========================
//   // Load Employees
//   // =========================
//   const loadResources = async () => {
//     try {
//       const data = await getResourcesByOrgId(user?.organisationId);
//       setResources(
//         (data || []).map((r) => ({
//           label: `${r.name} (${r.resourceId})`,
//           value: r.resourceId,
//           resourceId: r.resourceId,
//         })),
//       );
//     } catch {
//       toast.error("Failed to load employees");
//     }
//   };

//   // =========================
//   // Load Companies
//   // =========================
//   const loadCompanies = async () => {
//     const data = await getAllCompanyCodes(user.organisationId);
//     setCompanies(data || []);
//   };

//   useEffect(() => {
//     if (user?.organisationId) {
//       loadResources();
//       loadCompanies();
//     }
//   }, [user?.organisationId]);

//   // =========================
//   // Helpers
//   // =========================
//   const yearOptions = Array.from({ length: 5 }).map((_, i) => {
//     const y = moment().year() - i;
//     return { value: y, label: `Year ${y}` };
//   });

//   const months = selectedYear
//     ? Array.from({ length: 12 }).map((_, i) => {
//         const m = 11 - i;
//         return {
//           month: String(m + 1).padStart(2, "0"),
//           label: moment().year(selectedYear.value).month(m).format("MMMM YYYY"),
//         };
//       })
//     : [];

//   const extractCompanyCode = (workingDaysId) => workingDaysId.substring(4, 8);

//   // =========================
//   // Load Salary Slip
//   // =========================
//   const loadSalarySlip = async (month) => {
//     try {
//       setLoading(true);
//       setSelectedMonth(month);

//       const data = await getSalarySlip({
//         orgId: user.organisationId,
//         resourceId: selectedResource.resourceId,
//         year: selectedYear.value,
//         month: month.month,
//       });

//       setSalaryData(data);

//       const companyCode = extractCompanyCode(data.payroll.workingDaysId);
//       const matchedCompany = companies.find(
//         (c) => c.companyCode === companyCode,
//       );
//       setCompany(matchedCompany || null);
//     } catch (err) {
//       toast.error(err);
//       setSalaryData(null);
//       setCompany(null);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="main-content expand">
//       <Container fluid>
//         <h1 className="header-list-title">Salary Slip</h1>

//         {/* EMPLOYEE SELECT */}
//         <Card className="p-3 mt-3">
//           <Select
//             options={resources}
//             value={selectedResource}
//             onChange={(v) => {
//               setSelectedResource(v);
//               setSelectedYear(null);
//               setSelectedMonth(null);
//               setSalaryData(null);
//               setCompany(null);
//             }}
//             placeholder="Select Employee"
//             isClearable
//           />
//         </Card>

//         {selectedResource && (
//           <Row className="mt-3">
//             {/* LEFT */}
//             <Col md={4}>
//               <Card className="p-3">
//                 <Select
//                   options={yearOptions}
//                   value={selectedYear}
//                   onChange={(v) => {
//                     setSelectedYear(v);
//                     setSelectedMonth(null);
//                     setSalaryData(null);
//                   }}
//                   placeholder="Select Year"
//                 />

//                 {selectedYear && (
//                   <ListGroup className="mt-3">
//                     {months.map((m) => (
//                       <ListGroup.Item
//                         key={m.label}
//                         action
//                         active={selectedMonth?.label === m.label}
//                         onClick={() => loadSalarySlip(m)}
//                       >
//                         {m.label}
//                       </ListGroup.Item>
//                     ))}
//                   </ListGroup>
//                 )}
//               </Card>
//             </Col>

//             {/* RIGHT */}
//             <Col md={8}>
//               <Card className="p-4">
//                 {loading ? (
//                   <Spinner />
//                 ) : salaryData ? (
//                   <>
//                     {/* COMPANY HEADER */}
//                     <Row className="align-items-center mb-3">
//                       <Col md={3}>
//                         {company?.logoUrl && (
//                           <img
//                             src={company.logoUrl}
//                             alt="Company Logo"
//                             style={{ maxHeight: "8vw" }}
//                           />
//                         )}
//                       </Col>
//                       <Col md={9}>
//                         <h5>{company?.name}</h5>
//                         <small>Salary Slip – {selectedMonth.label}</small>
//                       </Col>
//                     </Row>

//                     <hr />

//                     {/* EMPLOYEE INFO */}
//                     <Row className="mb-3">
//                       <Col md={4}>
//                         <strong>Employee Name</strong>
//                         <div>{salaryData.employee.name}</div>
//                       </Col>
//                       <Col md={4}>
//                         <strong>Employee ID</strong>
//                         <div>{salaryData.employee.resourceId}</div>
//                       </Col>
//                       <Col md={4}>
//                         <strong>Department</strong>
//                         <div>{salaryData.employee.department}</div>
//                       </Col>
//                     </Row>

//                     <hr />

//                     {/* EARNINGS & DEDUCTIONS */}
//                     <Row>
//                       <Col md={6}>
//                         <h6>Earnings</h6>
//                         <Table bordered size="sm">
//                           <tbody>
//                             <tr>
//                               <td>Basic + DA</td>
//                               <td>₹{salaryData.payroll.basicPlusDa}</td>
//                             </tr>
//                             <tr>
//                               <td>HRA</td>
//                               <td>₹{salaryData.payroll.hra}</td>
//                             </tr>
//                             <tr>
//                               <td>Other Allowance</td>
//                               <td>₹{salaryData.payroll.otherAllowance}</td>
//                             </tr>
//                             <tr>
//                               <td>
//                                 <strong>Gross</strong>
//                               </td>
//                               <td>
//                                 <strong>₹{salaryData.payroll.gross}</strong>
//                               </td>
//                             </tr>
//                           </tbody>
//                         </Table>
//                       </Col>

//                       <Col md={6}>
//                         <h6>Deductions</h6>
//                         <Table bordered size="sm">
//                           <tbody>
//                             <tr>
//                               <td>PF</td>
//                               <td>₹{salaryData.payroll.pf}</td>
//                             </tr>
//                             <tr>
//                               <td>ESIC</td>
//                               <td>₹{salaryData.payroll.esic}</td>
//                             </tr>
//                             <tr>
//                               <td>TDS</td>
//                               <td>₹{salaryData.payroll.tdsdeductionAmount}</td>
//                             </tr>
//                             <tr>
//                               <td>
//                                 <strong>Total</strong>
//                               </td>
//                               <td>
//                                 <strong>
//                                   ₹{salaryData.payroll.totalDeductions}
//                                 </strong>
//                               </td>
//                             </tr>
//                           </tbody>
//                         </Table>
//                       </Col>
//                     </Row>

//                     <hr />

//                     {/* NET PAY */}
//                     <Row className="text-center">
//                       <Col>
//                         <h5>Net Pay: ₹{salaryData.payroll.netPayable}</h5>
//                         <small>
//                           Salary Days: {salaryData.payroll.salaryDays}
//                         </small>
//                       </Col>
//                     </Row>
//                   </>
//                 ) : (
//                   <div className="text-muted text-center">
//                     Select a month to view salary slip
//                   </div>
//                 )}
//               </Card>
//             </Col>
//           </Row>
//         )}
//       </Container>
//     </div>
//   );
// };

// export default SalarySlipHr;
